var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { Fragment, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { RadioButtons } from "../..";
import { CheckedStates } from "../../Checkbox";
import { useFormContext } from "../../Form";
import { StatusLabel } from "../../StatusLabel";
import { getBoolValueFromRow } from "../../table/v2/utilsV2Table";
import { useIconClasses } from "../../theming/icons";
import { setIcon } from "../../utils/icons";
export var SetRadioButton = observer(function (_a) {
    var options = _a.options, name = _a.name, dataId = _a.dataId, dataTestId = _a.dataTestId;
    var values = useFormContext().values;
    var value = toJS(values.get(name));
    var option = options === null || options === void 0 ? void 0 : options.filter(function (option) { return option.value === value; });
    if (option && option[0]) {
        return (React.createElement(StatusLabel, { text: option[0].label, color: option[0].readColor, dataId: dataId, dataTestId: dataTestId }));
    }
    return React.createElement("span", null, "-");
});
export var CheckedState = observer(function (_a) {
    var name = _a.name;
    var values = useFormContext().values;
    var set = toJS(values.get(name));
    var _b = setIcon("check"), iconType = _b.iconType, iconValue = _b.iconValue;
    var iconClasses = useIconClasses(iconType);
    if (set) {
        return (React.createElement(Fragment, null,
            React.createElement("span", { className: "".concat(iconClasses.icon, " ").concat(iconType, " ").concat(iconType, "-").concat(iconValue) }),
            React.createElement("span", { className: "sr-only" }, "Checked")));
    }
    return React.createElement("span", null, "-");
});
export var BulkCheckbox = observer(function (_a) {
    var name = _a.name, formComponent = _a.formComponent, dataTestId = _a.dataTestId, dataId = _a.dataId, label = _a.label;
    var columnKey = formComponent.id, onBulkChange = formComponent.onBulkChange, disabled = formComponent.disabled, hidden = formComponent.hidden;
    var values = useFormContext().values;
    var classes = useClasses();
    var collection = toJS(values.get(name)) || [];
    var handleChange = function (e) {
        var value = collection || [];
        onBulkChange && onBulkChange(e);
        value = value.map(function (item) {
            var checkboxCanBeSet = !(getBoolValueFromRow(item, disabled) || getBoolValueFromRow(item, hidden));
            if (checkboxCanBeSet) {
                var updatedValue = (item[columnKey] = e.target.checked);
                return __assign(__assign({}, item), { updatedValue: updatedValue });
            }
            return item;
        });
        values.set(name, value);
    };
    var someRowsAreInCollection = function () {
        return collection.filter(function (item) { return !!item[columnKey]; });
    };
    var allRowsAreInCollection = function () {
        return collection.every(function (item) {
            return item[columnKey] || getBoolValueFromRow(item, hidden);
        });
    };
    var allRowsAreDisabled = function () {
        return collection.every(function (item) {
            return getBoolValueFromRow(item, disabled) ||
                getBoolValueFromRow(item, hidden);
        });
    };
    var getCurrentState = function () {
        if (allRowsAreInCollection()) {
            return CheckedStates.Checked;
        }
        if (someRowsAreInCollection().length >= 1) {
            return CheckedStates.Indeterminate;
        }
        return CheckedStates.Unchecked;
    };
    var _b = useState(getCurrentState()), checked = _b[0], setChecked = _b[1];
    useEffect(function () { return setChecked(getCurrentState()); }, [collection]);
    return (React.createElement("label", null,
        React.createElement("span", { className: label === undefined ? "sr-only" : classes.label }, label !== null && label !== void 0 ? label : "Select All"),
        React.createElement("input", { name: "".concat(name, "_bulk"), type: "checkbox", onChange: handleChange, checked: checked === CheckedStates.Checked, disabled: allRowsAreDisabled(), ref: function (el) {
                return el && (el.indeterminate = checked === CheckedStates.Indeterminate);
            }, "data-testid": dataTestId && "".concat(dataTestId, "-").concat(checked), "data-id": dataId && "".concat(dataId, "-").concat(checked) })));
});
export var useClasses = function () {
    var useStyles = createUseStyles({
        label: {
            display: "block",
        },
    });
    return useStyles();
};
export var BulkRadioButtons = observer(function (_a) {
    var name = _a.name, formComponent = _a.formComponent, label = _a.label;
    var columnKey = formComponent.id, onBulkChange = formComponent.onBulkChange, disabled = formComponent.disabled, hidden = formComponent.hidden, options = formComponent.options;
    var componentName = "".concat(name, "_bulk");
    var values = useFormContext().values;
    var collection = toJS(values.get(name)) || [];
    var handleChange = function (option) {
        var value = collection;
        value = collection.map(function (item) {
            var checkboxCanBeSet = !(getBoolValueFromRow(item, disabled) || getBoolValueFromRow(item, hidden));
            if (checkboxCanBeSet) {
                var updatedValue = (item[columnKey] = option);
                return __assign(__assign({}, item), { updatedValue: updatedValue });
            }
            return item;
        });
        onBulkChange && onBulkChange(option);
        values.set(name, value);
    };
    var allRowsAreInCollection = function () {
        return collection.every(function (item) {
            return item[columnKey] === collection[0][columnKey];
        });
    };
    var getCurrentState = function () {
        var currentState = undefined;
        if (allRowsAreInCollection()) {
            currentState = collection[0][columnKey];
        }
        values.set(componentName, currentState);
    };
    useEffect(function () { return getCurrentState(); }, [collection]);
    return (React.createElement(RadioButtons, { label: label, name: componentName, type: "button", options: options, onChange: handleChange }));
});
